// services/adminStepUpPrompt.ts
//
// Single-flight admin step-up prompt (ORA-864). When an admin call comes back
// ADMIN_STEP_UP_REQUIRED, the axios client asks for a fresh step-up token here;
// every request that hits the wall while the prompt is open shares the same
// pending answer, so the admin verifies once and they all replay.
//
// The prompt UI (AdminStepUpModal) registers itself as the host while it is
// mounted. With no host on the page (e.g. a public route outside
// ProtectedLayout) a request fails at once instead of waiting forever.
import { store } from '../store/store';
import {
  AdminStepUpPromptReason,
  adminStepUpPromptClosed,
  adminStepUpPromptOpened,
} from '../slices/adminStepUp/adminStepUpSlice';
import { AdminStepUpCancelledError } from './adminStepUpStorage';

export interface AdminStepUpPromptDeps {
  open: (reason: AdminStepUpPromptReason) => void;
  close: () => void;
}

/** No step-up prompt is mounted on this page, so nobody can answer one. */
export class AdminStepUpUnavailableError extends AdminStepUpCancelledError {
  constructor() {
    super();
    this.name = 'AdminStepUpUnavailableError';
  }
}

export interface AdminStepUpPrompt {
  /** Opens the prompt (or joins the open one) and resolves with the token. */
  request: (reason?: AdminStepUpPromptReason) => Promise<string>;
  /** The admin verified: every waiting request gets the token. */
  resolve: (token: string) => void;
  /** The admin closed the prompt: every waiting request is rejected. */
  cancel: () => void;
  /**
   * Marks a prompt UI as mounted. Returns the unregister function; when the
   * last host goes away, any open prompt is cancelled.
   */
  registerHost: () => () => void;
}

/** The prompt logic with the UI wiring injected, so it is unit-testable. */
export const createAdminStepUpPrompt = (
  deps: AdminStepUpPromptDeps
): AdminStepUpPrompt => {
  let pending: {
    promise: Promise<string>;
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  } | null = null;

  let hostCount = 0;

  const settle = () => {
    const current = pending;
    pending = null;
    deps.close();
    return current;
  };

  const cancel = () => {
    if (!pending) return;
    settle()?.reject(new AdminStepUpCancelledError());
  };

  return {
    request: (reason = 'required') => {
      if (pending) return pending.promise;
      if (hostCount === 0) {
        return Promise.reject(new AdminStepUpUnavailableError());
      }

      let resolve!: (token: string) => void;
      let reject!: (error: Error) => void;

      const promise = new Promise<string>((res, rej) => {
        resolve = res;
        reject = rej;
      });

      pending = { promise, resolve, reject };
      deps.open(reason);
      return promise;
    },
    resolve: (token) => {
      settle()?.resolve(token);
    },
    cancel,
    registerHost: () => {
      hostCount += 1;
      let isRegistered = true;

      return () => {
        if (!isRegistered) return;
        isRegistered = false;
        hostCount -= 1;
        if (hostCount === 0) cancel();
      };
    },
  };
};

const adminStepUpPrompt = createAdminStepUpPrompt({
  open: (reason) => store.dispatch(adminStepUpPromptOpened(reason)),
  close: () => store.dispatch(adminStepUpPromptClosed()),
});

export const requestAdminStepUp = adminStepUpPrompt.request;
export const resolveAdminStepUp = adminStepUpPrompt.resolve;
export const cancelAdminStepUp = adminStepUpPrompt.cancel;
export const registerAdminStepUpPromptHost = adminStepUpPrompt.registerHost;
